import { TfNotification, useActionNotifications, useDeleteNotifications, useUpdateNotifications } from 'api';
import { usePermission } from 'auth';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import { NotificationTypeEnum, UserPermissionsEnum } from 'oas';
import * as React from 'react';
import { ButtonIcon, Dropdown, DropdownItem, IconLoader } from 'ui';

interface ActivityMenuProps {
    groupKey: TfNotification['groupKey'];
    notifications: TfNotification[];
}

export const ActivityMenu: React.FC<ActivityMenuProps> = ({ groupKey, notifications }) => {
    const { __ } = useTranslations();
    const { mutate: deleteNotifications } = useDeleteNotifications();
    const { mutate: updateNotifications } = useUpdateNotifications();
    const { mutate: runActions } = useActionNotifications();
    const groupNotifications = notifications.filter((notification) => notification.groupKey === groupKey);
    const groupNotificationIds = groupNotifications.map((notification) => notification.id);
    const hasUnread = groupNotifications.some((notification) => !notification.read);
    const hasActionable = groupNotifications.some((notification) => notification.type !== NotificationTypeEnum.Info);
    const canAction = usePermission(UserPermissionsEnum.SettingsBands);

    function dismissAll() {
        deleteNotifications(groupNotificationIds);
        mixpanel.track('BellNotificationActivityDismissAllClicked');
    }

    function updateAll() {
        runActions(groupNotificationIds);
        const props: Record<string, number> = {};
        for (const { type } of groupNotifications) {
            props[`athlete_count_${type}`] = (props[`athlete_count_${type}`] ?? 0) + 1;
        }
        mixpanel.track('BellNotificationActivityActionAllClicked', props);
    }

    function markAllAsRead() {
        updateNotifications({ ids: groupNotificationIds, read: true });
        mixpanel.track('BellNotificationActivityMarkAllAsReadClicked');
    }

    const menuConfig = [
        { label: __('misc.update_all'), onClick: updateAll, show: canAction && hasActionable },
        { label: __('misc.dismiss_all'), onClick: dismissAll, show: canAction },
        { label: __('notifications.mark_all_as_read'), onClick: markAllAsRead, show: hasUnread },
    ];

    const shownItems = menuConfig.filter((item) => item.show);

    return shownItems.length ? (
        <Dropdown
            label=""
            placement="bottom-end"
            testId={`activity-menu-${groupKey}`}
            renderTrigger={() => (
                <ButtonIcon
                    size="sm"
                    color="ghost"
                    id={`activity-menu-button-${groupKey}`}
                    ariaLabel={`${__('misc.options')}: ${groupNotifications[0]?.activities?.[0]?.name}`}
                >
                    <IconLoader iconName="IconMore" size="sm" aria-hidden />
                </ButtonIcon>
            )}
        >
            {shownItems.map((item) => (
                <DropdownItem key={item.label} onClick={item.onClick}>
                    {item.label}
                </DropdownItem>
            ))}
        </Dropdown>
    ) : (
        <></>
    );
};
